import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { useAuth, axiosInstance as axios } from '../context/AuthContext';
import io from 'socket.io-client';
import { FaArrowLeft, FaHome, FaMoon } from 'react-icons/fa';

// Import extracted components
import { ConversationList, ChatView, QuietHoursModal, NewDMModal } from '../components/messages';
import { BrandLogo } from '../components/BrandLogo';

const BACKEND_URL = '';
const API = `${BACKEND_URL}/api`;

export default function Messages() {
  const navigate = useNavigate();
  const { conversationId } = useParams();
  const { user } = useAuth();
  
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewDM, setShowNewDM] = useState(false);
  const [familyMembers, setFamilyMembers] = useState([]);
  const [socket, setSocket] = useState(null);
  const [typingUsers, setTypingUsers] = useState([]);
  const [showQuietHours, setShowQuietHours] = useState(false);
  const [quietHours, setQuietHours] = useState(null);

  // Fetch quiet hours settings
  useEffect(() => {
    const fetchQuietHours = async () => {
      try {
        const response = await axios.get(`${API}/user/quiet-hours`, { withCredentials: true });
        setQuietHours(response.data);
      } catch (error) {
        console.error('Error fetching quiet hours:', error);
      }
    };
    fetchQuietHours();
  }, []);

  // Save quiet hours settings
  const handleSaveQuietHours = async (settings) => {
    try {
      const response = await axios.put(`${API}/user/quiet-hours`, settings, { withCredentials: true });
      setQuietHours(response.data);
    } catch (error) {
      console.error('Error saving quiet hours:', error);
    }
  };

  // Fetch conversations
  useEffect(() => {
    const fetchConversations = async () => {
      try {
        const response = await axios.get(`${API}/conversations`, { withCredentials: true });
        setConversations(response.data);
        
        // If conversationId in URL, select that conversation
        if (conversationId) {
          const conv = response.data.find(c => c.conversation_id === conversationId);
          if (conv) {
            setSelectedConversation(conv);
          }
        }
      } catch (error) {
        console.error('Error fetching conversations:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchConversations();
  }, [conversationId]);

  // Socket.IO connection
  useEffect(() => {
    const newSocket = io(BACKEND_URL, {
      transports: ['websocket', 'polling']
    });

    newSocket.on('connect', () => {
      
      // Join user's personal notification room
      if (user?.user_id) {
        newSocket.emit('join_user_room', { user_id: user.user_id });
      }
    });

    newSocket.on('new_conversation_message', (message) => {
      if (selectedConversation && message.conversation_id === selectedConversation.conversation_id) {
        setMessages(prev => {
          const exists = prev.some(m => m.message_id === message.message_id);
          if (exists) return prev;
          return [...prev, message];
        });
        
        // Clear typing indicator for this user
        setTypingUsers(prev => prev.filter(t => t.user_id !== message.sender_id));
      }
      
      // Update conversation list
      setConversations(prev => prev.map(conv => {
        if (conv.conversation_id === message.conversation_id) {
          return {
            ...conv,
            last_message: {
              message_id: message.message_id,
              sender_id: message.sender_id,
              sender_name: message.sender_name,
              content: message.content,
              created_at: message.created_at
            },
            unread_count: conv.conversation_id === selectedConversation?.conversation_id 
              ? 0 
              : conv.unread_count + 1
          };
        }
        return conv;
      }));
    });

    // Handle typing indicators
    newSocket.on('user_typing', (data) => {
      if (selectedConversation && data.conversation_id === selectedConversation.conversation_id) {
        if (data.is_typing) {
          setTypingUsers(prev => {
            const exists = prev.some(t => t.user_id === data.user_id);
            if (exists) return prev;
            return [...prev, { user_id: data.user_id, user_name: data.user_name }];
          });
        } else {
          setTypingUsers(prev => prev.filter(t => t.user_id !== data.user_id));
        }
      }
    });

    // Handle read receipts
    newSocket.on('message_read', (data) => {
      if (selectedConversation && data.conversation_id === selectedConversation.conversation_id) {
        setMessages(prev => prev.map(msg => {
          if (msg.message_id === data.message_id) {
            const readBy = msg.read_by || [];
            if (!readBy.some(r => r.user_id === data.user_id)) {
              return {
                ...msg,
                read_by: [...readBy, { user_id: data.user_id, user_name: data.user_name, read_at: data.read_at }]
              };
            }
          }
          return msg;
        }));
      }
    });

    setSocket(newSocket);

    return () => {
      newSocket.disconnect();
    };
  }, [selectedConversation, user]);

  // Clear typing users when switching conversations
  useEffect(() => {
    setTypingUsers([]);
  }, [selectedConversation?.conversation_id]);

  // Join/leave conversation room
  useEffect(() => {
    if (socket && selectedConversation) {
      socket.emit('join_conversation', { conversation_id: selectedConversation.conversation_id });
      
      return () => {
        socket.emit('leave_conversation', { conversation_id: selectedConversation.conversation_id });
      };
    }
  }, [socket, selectedConversation]);

  // Fetch messages when conversation selected
  useEffect(() => {
    const fetchMessages = async () => {
      if (!selectedConversation) return;
      
      try {
        const response = await axios.get(
          `${API}/conversations/${selectedConversation.conversation_id}/messages`,
          { withCredentials: true }
        );
        setMessages(response.data);
      } catch (error) {
        console.error('Error fetching messages:', error);
      }
    };

    fetchMessages();
  }, [selectedConversation]);

  // Mark all messages as read
  const handleMarkAllRead = useCallback(async (conversationId) => {
    try {
      await axios.post(
        `${API}/conversations/${conversationId}/mark-all-read`,
        {},
        { withCredentials: true }
      );
    } catch (error) {
      console.error('Error marking messages as read:', error);
    }
  }, []);

  // Fetch family members for new DM
  const fetchFamilyMembers = async () => {
    try {
      // Get all families user is part of
      const familiesResponse = await axios.get(`${API}/families`, { withCredentials: true });
      const allMembers = new Map();
      
      for (const family of familiesResponse.data) {
        const membersResponse = await axios.get(
          `${API}/families/${family.family_id}/members/for-dm`,
          { withCredentials: true }
        );
        membersResponse.data.forEach(member => {
          if (!allMembers.has(member.user_id)) {
            allMembers.set(member.user_id, member);
          }
        });
      }
      
      setFamilyMembers(Array.from(allMembers.values()));
    } catch (error) {
      console.error('Error fetching family members:', error);
    }
  };

  const handleSelectConversation = (conv) => {
    setSelectedConversation(conv);
    navigate(`/messages/${conv.conversation_id}`);
    
    // Reset unread count
    setConversations(prev => prev.map(c => 
      c.conversation_id === conv.conversation_id ? { ...c, unread_count: 0 } : c
    ));
  };

  const handleSendMessage = async (content, priority = 'normal', mentions = []) => {
    if (!selectedConversation) return;
    
    try {
      const response = await axios.post(
        `${API}/conversations/${selectedConversation.conversation_id}/messages`,
        { content, priority, mentions },
        { withCredentials: true }
      );
      
      // Add message optimistically (will also come via socket)
      setMessages(prev => {
        const exists = prev.some(m => m.message_id === response.data.message_id);
        if (exists) return prev;
        return [...prev, response.data];
      });
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  const handleMute = async (isMuted, duration) => {
    if (!selectedConversation) return;
    
    try {
      const response = await axios.put(
        `${API}/conversations/${selectedConversation.conversation_id}/settings`,
        { is_muted: isMuted, duration },
        { withCredentials: true }
      );
      
      // Update conversation in state
      setSelectedConversation(prev => ({
        ...prev,
        is_muted: response.data.is_muted,
        mute_until: response.data.mute_until
      }));
      
      setConversations(prev => prev.map(c => 
        c.conversation_id === selectedConversation.conversation_id 
          ? { ...c, is_muted: response.data.is_muted, mute_until: response.data.mute_until }
          : c
      ));
    } catch (error) {
      console.error('Error updating mute settings:', error);
    }
  };

  const handleNewDM = () => {
    fetchFamilyMembers();
    setShowNewDM(true);
  };

  const handleSelectMemberForDM = async (member) => {
    try {
      const response = await axios.post(
        `${API}/conversations/dm?other_user_id=${member.user_id}`,
        {},
        { withCredentials: true }
      );
      
      // Add to conversations if not exists
      const existingConv = conversations.find(c => c.conversation_id === response.data.conversation_id);
      if (!existingConv) {
        const newConv = {
          ...response.data,
          display_name: member.name,
          other_participant: member,
          is_muted: false,
          unread_count: 0
        };
        setConversations(prev => [newConv, ...prev]);
        setSelectedConversation(newConv);
      } else {
        setSelectedConversation(existingConv);
      }
      
      setShowNewDM(false);
      navigate(`/messages/${response.data.conversation_id}`);
    } catch (error) {
      console.error('Error creating DM:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link to="/dashboard" className="flex items-center space-x-2 hover:opacity-80 transition" data-testid="logo-link">
                <BrandLogo className="w-9 h-9" testId="messages-logo" />
                <span className="text-xl font-bold text-gray-800 hidden sm:inline">Legacy Forge Family</span>
              </Link>
              <span className="text-gray-300">|</span>
              <button
                onClick={() => navigate('/dashboard')}
                className="text-gray-600 hover:text-gray-800"
                data-testid="back-btn"
              >
                <FaArrowLeft className="text-xl" />
              </button>
              <h1 className="text-xl font-bold text-gray-800">Messages</h1>
              {quietHours?.enabled && (
                <span className="flex items-center text-xs text-indigo-600 bg-indigo-50 px-2 py-1 rounded-full">
                  <FaMoon className="mr-1" /> Quiet Hours
                </span>
              )}
            </div>
            
            <Link
              to="/"
              className="flex items-center space-x-2 px-3 py-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition text-xs sm:text-sm"
              data-testid="home-link"
            >
              <FaHome />
              <span>Home</span>
            </Link>
          </div>
        </div>
      </header>
      
      {/* Main Content */}
      <main className="flex-1 container mx-auto px-4 py-4">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden h-[calc(100vh-140px)] flex">
          {/* Conversation List - Left Panel */}
          <div className="w-80 border-r border-gray-200 flex-shrink-0">
            <ConversationList
              conversations={conversations}
              selectedId={selectedConversation?.conversation_id}
              onSelect={handleSelectConversation}
              onNewDM={handleNewDM}
              onOpenSettings={() => setShowQuietHours(true)}
            />
          </div>
          
          {/* Chat View - Right Panel */}
          <div className="flex-1">
            <ChatView
              conversation={selectedConversation}
              messages={messages}
              onSendMessage={handleSendMessage}
              onMute={handleMute}
              currentUser={user}
              socket={socket}
              typingUsers={typingUsers}
              onMarkRead={handleMarkAllRead}
            />
          </div>
        </div>
      </main>
      
      {/* New DM Modal */}
      <NewDMModal
        isOpen={showNewDM}
        onClose={() => setShowNewDM(false)}
        familyMembers={familyMembers}
        onSelectMember={handleSelectMemberForDM}
      />
      
      {/* Quiet Hours Settings Modal */}
      <QuietHoursModal
        isOpen={showQuietHours}
        onClose={() => setShowQuietHours(false)}
        quietHours={quietHours}
        onSave={handleSaveQuietHours}
      />
    </div>
  );
}
